import { useEffect, useState } from 'react';
import { ChevronDown, Minus, Plus, Sparkles, SquareDashed, Palette, Wand2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SelectionOpsPanel } from './SelectionOpsPanel';

/** Preset zoom levels surfaced in the StatusBar's % dropdown.
 *  Matches Photoshop's standard ladder. "Fit" maps to
 *  `fitToContainer` instead of an absolute zoom. */
const ZOOM_PRESETS: ReadonlyArray<{ label: string; zoom: number | 'fit' }> = [
  { label: 'Fit on Screen', zoom: 'fit' },
  { label: '10%', zoom: 0.1 },
  { label: '25%', zoom: 0.25 },
  { label: '50%', zoom: 0.5 },
  { label: '100%', zoom: 1 },
  { label: '200%', zoom: 2 },
  { label: '400%', zoom: 4 },
  { label: '800%', zoom: 8 },
];

/** Format the zoom multiplier as a display percentage. Shows
 *  integer when the value is at (or within float-epsilon of) a
 *  whole percent so 1.0 reads as `100%` not `100.0%`, and 0.1
 *  reads as `10%` despite `0.1 * 100 === 10.000000000000002` in
 *  IEEE 754. Otherwise shows one decimal so a wheel-zoom tick
 *  landing on 99.5 doesn't round up to a misleading `100%`. */
function formatZoomPercent(zoom: number): string {
  const pct = zoom * 100;
  const rounded = Math.round(pct);
  if (Math.abs(pct - rounded) < 0.01) return `${rounded}%`;
  return `${pct.toFixed(1)}%`;
}

/** Props the StatusBar's selection chip needs from EditorPage. When
 *  `hasSelection` is false, the modify-selection rows hide
 *  themselves; Quick Mask / Color Range / AI Select Subject stay
 *  reachable because each can CREATE a selection.
 *
 *  Quick Mask toggling lives here (not on a dedicated chip) per the
 *  Tier 1 Group D roadmap entry: "Toggle from status-bar selection
 *  chip popover." */
interface SelectionChipState {
  hasSelection: boolean;
  width: number;
  height: number;
  canGrow: boolean;
  onGrow: (deltaPx: number) => void;
  onInvert: () => void;
  /** AI Select Subject — null hides the entry. Currently always null:
   *  the on-device imgly segmentation backend was retired for
   *  licensing reasons and has no replacement yet. */
  onSelectSubject?: (() => void) | null;
  selectSubjectBusy?: boolean;
  /** Quick Mask toggle. The bar reads `quickMaskActive` to flip the
   *  chip label between "Selection…" and "Quick Mask · Done". */
  quickMaskActive: boolean;
  onToggleQuickMask: () => void;
  /** Refine Edge — only meaningful when there's a selection. Hidden
   *  via `hasSelection` gate in the popover. */
  onOpenRefineEdge?: () => void;
  /** Color Range — always available; creates a new selection. */
  onOpenColorRange?: () => void;
}

interface StatusBarProps {
  zoom: number;
  canvasWidth: number;
  canvasHeight: number;
  /** Subscribe-style cursor source. The bar manages its own
   *  cursor state locally; the canvas hook fires the callback
   *  on every `mouse:move` (60+ Hz) and only this component
   *  re-renders. Returns an unsubscribe; lifecycle handled in
   *  a useEffect. */
  subscribeToCursorMove: (cb: (x: number, y: number) => void) => () => void;
  currentColor: string;
  /** Selection chip — null hides the chip entirely (no image
   *  loaded yet). With an image present, the chip is always
   *  visible so Quick Mask and Color Range stay reachable. */
  selection?: SelectionChipState | null;
  /** Optional zoom-control callbacks. When supplied, the StatusBar
   *  renders +/- buttons + a preset dropdown next to the zoom %.
   *  Omit (or pass null) to keep the bar read-only — useful in
   *  test harnesses or restricted-permission views. */
  zoomControls?: {
    onZoomIn: () => void;
    onZoomOut: () => void;
    onZoomTo: (zoom: number) => void;
    onZoomFit: () => void;
  } | null;
}

export function StatusBar({
  zoom,
  canvasWidth,
  canvasHeight,
  subscribeToCursorMove,
  currentColor,
  selection,
  zoomControls,
}: StatusBarProps) {
  const [cursor, setCursor] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  useEffect(() => {
    return subscribeToCursorMove((x, y) => setCursor({ x, y }));
  }, [subscribeToCursorMove]);
  return (
    <div className="flex items-center h-7 bg-background border-t border-border px-3 text-xs text-muted-foreground gap-4 select-none">
      {zoomControls ? (
        <span className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={zoomControls.onZoomOut}
            aria-label="Zoom out"
            className="h-5 w-5 inline-flex items-center justify-center rounded text-foreground hover:bg-accent disabled:opacity-50"
            disabled={zoom <= 0.1}
          >
            <Minus className="h-3 w-3" />
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Zoom presets"
                className="px-1.5 h-5 inline-flex items-center gap-0.5 rounded tabular-nums text-foreground hover:bg-accent"
              >
                {formatZoomPercent(zoom)}
                <ChevronDown className="h-3 w-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-44 p-1" align="start" side="top">
              {ZOOM_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => {
                    if (p.zoom === 'fit') zoomControls.onZoomFit();
                    else zoomControls.onZoomTo(p.zoom);
                  }}
                  className="w-full text-left px-2 py-1 text-xs rounded hover:bg-accent text-foreground"
                >
                  {p.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <button
            type="button"
            onClick={zoomControls.onZoomIn}
            aria-label="Zoom in"
            className="h-5 w-5 inline-flex items-center justify-center rounded text-foreground hover:bg-accent disabled:opacity-50"
            disabled={zoom >= 10}
          >
            <Plus className="h-3 w-3" />
          </button>
        </span>
      ) : (
        <span className="tabular-nums">{formatZoomPercent(zoom)}</span>
      )}
      <span className="w-px h-3.5 bg-border" />
      <span className="tabular-nums">
        {canvasWidth} x {canvasHeight}
      </span>
      <span className="w-px h-3.5 bg-border" />
      <span className="tabular-nums">
        X: {cursor.x} Y: {cursor.y}
      </span>
      {/* Color indicator — hidden on narrow (phone) viewports to free
          bottom-bar room; restored at `sm:` and up via display:contents
          so the divider + swatch rejoin the flex row unchanged. */}
      <span className="hidden sm:contents">
        <span className="w-px h-3.5 bg-border" />
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-3.5 h-3.5 rounded border border-border"
            style={{ backgroundColor: currentColor }}
          />
          <span className="tabular-nums uppercase">{currentColor}</span>
        </span>
      </span>
      {selection && (
        // Selection chip — same narrow-viewport hide. Quick Mask / Color
        // Range / Refine Edge live behind this popover, so it stays
        // reachable on tablet/desktop where there's horizontal room.
        <span className="hidden sm:contents">
          <span className="w-px h-3.5 bg-border" />
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 text-foreground hover:bg-accent rounded px-1.5 py-0.5"
                aria-label="Selection menu"
              >
                <span className="tabular-nums">
                  {selection.quickMaskActive
                    ? 'Quick Mask · Done'
                    : selection.hasSelection
                      ? `Selection: ${Math.round(selection.width)}×${Math.round(selection.height)}`
                      : 'Select'}
                </span>
                <ChevronDown className="h-3 w-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-3 space-y-3" align="end">
              {/* Tools at the top — these CREATE a selection so
                  they make sense regardless of whether one exists.
                  Quick Mask gets a distinct visual treatment when
                  active (primary tint) so the user sees the mode
                  state at a glance. */}
              <div className="space-y-1">
                <button
                  type="button"
                  onClick={selection.onToggleQuickMask}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs ${
                    selection.quickMaskActive
                      ? 'bg-primary text-primary-foreground hover:opacity-90'
                      : 'hover:bg-accent text-foreground'
                  }`}
                >
                  <SquareDashed className="h-3.5 w-3.5" />
                  <span>{selection.quickMaskActive ? 'Exit Quick Mask' : 'Quick Mask'}</span>
                  <span className="ml-auto text-[10px] opacity-70">Q</span>
                </button>
                {selection.onOpenColorRange && !selection.quickMaskActive && (
                  <button
                    type="button"
                    onClick={selection.onOpenColorRange}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-accent text-foreground"
                  >
                    <Palette className="h-3.5 w-3.5" />
                    <span>Color Range…</span>
                  </button>
                )}
                {selection.onSelectSubject && !selection.quickMaskActive && (
                  <button
                    type="button"
                    onClick={selection.onSelectSubject}
                    disabled={selection.selectSubjectBusy}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-accent text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>
                      {selection.selectSubjectBusy ? 'Selecting subject…' : 'AI Select Subject'}
                    </span>
                  </button>
                )}
              </div>

              {/* Modify section — only meaningful with a selection.
                  Refine Edge sits here too because it refines an
                  existing selection. */}
              {selection.hasSelection && !selection.quickMaskActive && (
                <div className="space-y-2 pt-2 border-t border-border">
                  <SelectionOpsPanel
                    hasSelection
                    canGrow={selection.canGrow}
                    onGrow={selection.onGrow}
                    onInvert={selection.onInvert}
                    compact
                  />
                  {selection.onOpenRefineEdge && (
                    <button
                      type="button"
                      onClick={selection.onOpenRefineEdge}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-accent text-foreground border border-border"
                    >
                      <Wand2 className="h-3.5 w-3.5" />
                      <span>Refine Edge…</span>
                    </button>
                  )}
                </div>
              )}
            </PopoverContent>
          </Popover>
        </span>
      )}
    </div>
  );
}
